// Notification bell + dropdown for the admin SPA topbar.
// Polls /notifications/me/unread-count every 60s. Lazy-loads the full
// list when the dropdown opens.
import { useCallback, useEffect, useRef, useState } from "react";
import { NotificationsApi, type NotificationItem } from "../api/notifications";


const formatRelative = (iso: string): string => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "только что";
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  return new Date(iso).toLocaleDateString("ru-RU");
};


export const NotificationsBell = () => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try {
      const r = await NotificationsApi.unreadCount();
      setCount(r.data.count);
    } catch {
      // unauthenticated -- bell will hide via auth context elsewhere
    }
  }, []);

  useEffect(() => {
    void refreshCount();
    const id = setInterval(refreshCount, 60_000);
    return () => clearInterval(id);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    NotificationsApi.list()
      .then((r) => setItems(r.data.slice(0, 10)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const onRead = async (id: number) => {
    await NotificationsApi.markRead(id).catch(() => {});
    setItems((arr) => arr.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    void refreshCount();
  };

  const onReadAll = async () => {
    await NotificationsApi.markAllRead().catch(() => {});
    setItems((arr) => arr.map((n) => ({ ...n, is_read: true })));
    setCount(0);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
        aria-label="Уведомления"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {count > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold leading-none text-white"
            aria-live="polite"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <span className="text-sm font-semibold text-slate-900">Уведомления</span>
            {count > 0 && (
              <button
                type="button"
                onClick={onReadAll}
                className="text-xs font-medium text-brand hover:underline"
              >
                Прочитать все
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="px-4 py-6 text-center text-sm text-slate-500">Загрузка...</div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                Уведомлений нет.
              </div>
            )}
            {!loading && items.length > 0 && (
              <ul>
                {items.map((n) => (
                  <li
                    key={n.id}
                    className={`border-b border-slate-100 px-4 py-3 text-sm last:border-b-0 ${
                      n.is_read ? "" : "bg-brand/5"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{n.title}</div>
                        <div className="text-slate-600">{n.message}</div>
                        <div className="mt-1 text-xs text-slate-400">{formatRelative(n.created_at)}</div>
                      </div>
                      {!n.is_read && (
                        <button
                          type="button"
                          onClick={() => onRead(n.id)}
                          className="shrink-0 text-xs font-medium text-brand hover:underline"
                          aria-label="Отметить прочитанным"
                        >
                          ✓
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
